import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ReactPaginate from 'react-paginate';
import * as API from "../Endpoints/Endpoints";

const GameAllocationTable = () => {
    const [allocations, setAllocations] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [categories, setCategories] = useState([]); // Store unique categories
    const [selectedCategory, setSelectedCategory] = useState(''); // Selected category for filtering
    const itemsPerPage = 5;

    const navigate = useNavigate();

    useEffect(() => {
        getData();
        fetchCategoriesAndNames();
    }, []);

    const getData = () => {
        axios({
            url: API.GET_ALLOCATION_API,
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        }).then((res) => {
            setAllocations(res.data);
            const uniqueCategories = [
                ...new Set(res.data.map((allocation) => allocation.gamecategory)),
            ];
            setCategories(uniqueCategories);
        }).catch((err) => {
            alert("Failure: " + err);
        });
    };
   
    const fetchCategoriesAndNames = async () => {
        try {
            const res = await axios.get(API.FETCH_DATA_API);
            setCategories(res.data.categories || []); // Ensure categories is set to an array
        } catch (err) {
            console.error('Failed to fetch categories:', err);
            alert('Failed to fetch category data.');
        }
    };

    const DeleteAlloc = (cardallocid) => {
        if (window.confirm("Are you sure you want to delete this allocation?")) {
            axios({
                url: API.DELETE_ALLOCATION_API(cardallocid),
                method: 'DELETE',
            }).then((res) => {
                alert("Deleted allocation successfully");
                getData();
            }).catch((err) => {
                alert("Error: " + err);
            });
        }
    };

    const ViewAllocation = (cardallocid) => {
        navigate(`/viewalloc/${cardallocid}`);
    };

    const UpdateAllocation = (cardallocid) => {
        navigate(`/updatealloc/${cardallocid}`);
    };

    const handlePageClick = (data) => {
        setCurrentPage(data.selected);
    };

    const handleCategoryChange = (e) => {
        setSelectedCategory(e.target.value);
    };

    const handleUpload = (e) => {
        const file = e.target.files[0];
        console.log(file); // Log the selected file
    
        if (file) {
            if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
                alert("Please upload an Excel file (xlsx or xls).");
                return;
            }
    
            const formData = new FormData();
            formData.append('file', file);
    
            // Upload the file with progress tracking
            axios.post(API.UPLOAD_ALLOCATION_API, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                onUploadProgress: (progressEvent) => {
                    let percent = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                    console.log(`Upload progress: ${percent}%`);
                    // You can also update a progress bar here with `percent`
                }
            }).then((res) => {
                alert("File uploaded and allocations added successfully");
                getData(); // Refresh data after upload
            }).catch((err) => {
                console.error("Upload error:", err.response?.data || err.message);
                alert("Error uploading file: " + (err.response?.data?.message || err.message));
            });
        } else {
            alert("No file selected. Please choose a file to upload.");
        }
    };
    

    const offset = currentPage * itemsPerPage;

    const filteredAllocations = allocations.filter((allocation) => {
        const matchesSearch = allocation.gamecategory.toLowerCase().includes(searchTerm.toLowerCase())
            || allocation.gamename.toLowerCase().includes(searchTerm.toLowerCase())
            || allocation.cardgroup.toLowerCase().includes(searchTerm.toLowerCase())
            || allocation.cardname.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = !selectedCategory || allocation.gamecategory === selectedCategory;
        return matchesSearch && matchesCategory ;
    });

    const currentPageData = filteredAllocations.slice(offset, offset + itemsPerPage);

    const highlightText = (text, term) => {
        if (!term) return text;
        const parts = text.split(new RegExp(`(${term})`, 'gi'));
        return parts.map((part, index) =>
            part.toLowerCase() === term.toLowerCase() ? (
                <span key={index} style={{ backgroundColor: 'yellow' }}>{part}</span>
            ) : part
        );
    };

    return (
        <div className='bg-[#B5B5B5] h-[auto] pb-20 pt-10'>
            <div className='p-4 flex justify-center space-x-4'>
                <button className="bg-[#e64a32] hover:bg-[#e64a32] text-white font-bold py-2 px-5 rounded-full" onClick={() => navigate("/addcardalloc")}>
                    Add Allocation
                </button>
                <label className="bg-[#e64a32] hover:bg-[#e64a32] text-white font-bold py-2 px-5 rounded-full cursor-pointer">
                    Upload File
                    <input type="file" accept=".xlsx, .xls" className="hidden" onChange={handleUpload} />
                </label>
            </div>

            {/* Search box */}
            <div className="max-w-4xl mx-auto pt-4">
                <input
                    type="text"
                    placeholder="Search allocations..."
                    className="w-full px-3 py-2 mb-4 text-gray-700 border rounded-lg focus:outline-none focus:border-[#e64a32]"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            {/* Dropdown for filtering categories */}
            <div className="max-w-4xl mx-auto pt-4">
                 <select value={selectedCategory} onChange={handleCategoryChange}>
                <option value="">All Categories</option>
                {categories.map((category, index) => (
                    <option key={index} value={category}>
                        {category}
                    </option>
                ))}
            </select>
            </div>           

            {/* Allocation Table */}
            <div className="max-w-4xl mx-auto pt-[30px]">
                <div className="flex flex-col">
                    <div className="overflow-x-auto shadow-md sm:rounded-lg">
                        <div className="inline-block min-w-full align-middle">
                            <div className="overflow-hidden">
                                <table className="min-w-full divide-y divide-gray-200 table-fixed dark:divide-gray-700">
                                    <thead className="bg-white dark:bg-white font-bold text-black">
                                        <tr>
                                            <th scope="col" className="py-3 px-6 text-xs tracking-wider text-center text-gray-700 uppercase dark:text-gray-400">Game Category</th>
                                            <th scope="col" className="py-3 px-6 text-xs tracking-wider text-center text-gray-700 uppercase dark:text-gray-400">Game Name</th>
                                            <th scope="col" className="py-3 px-6 text-xs tracking-wider text-center text-gray-700 uppercase dark:text-gray-400">Card Group</th>
                                            <th scope="col" className="py-3 px-6 text-xs tracking-wider text-center text-gray-700 uppercase dark:text-gray-400">Card Name</th>
                                            <th scope="col" className="py-3 px-6 text-xs tracking-wider text-center text-gray-700 uppercase dark:text-gray-400">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-800 dark:divide-gray-700">
                                        {currentPageData.map((allocation, index) => (
                                            <tr className="hover:bg-gray-100 dark:hover:bg-gray-700" key={allocation.cardallocid}>
                                                {/* <td className="py-4 px-6 text-sm font-medium text-gray-900 dark:text-white">{allocation.cardallocid}</td> */}
                                                <td className="py-4 px-6 text-sm text-gray-900 dark:text-white">{highlightText(allocation.gamecategory, searchTerm)}</td>
                                                <td className="py-4 px-6 text-sm text-gray-900 dark:text-white">{highlightText(allocation.gamename, searchTerm)}</td>
                                                <td className="py-4 px-6 text-sm text-gray-900 dark:text-white">{highlightText(allocation.cardgroup, searchTerm)}</td>
                                                <td className="py-4 px-6 text-sm text-gray-900 dark:text-white">{highlightText(allocation.cardname, searchTerm)}</td>
                                                <td className="py-4 px-6 text-sm font-medium text-center text-gray-900 dark:text-white">
                                                <button onClick={() => ViewAllocation(allocation.cardallocid)} className="bg-[#e64a32] hover:bg-[#e64a32] text-white font-bold py-2 px-4 rounded-full ml-1">View</button>
                                                    <button onClick={() => UpdateAllocation(allocation.cardallocid)} className="bg-[#e64a32] hover:bg-[#e64a32] text-white font-bold py-2 px-4 rounded-full ml-1">Edit</button>
                                                    <button onClick={() => DeleteAlloc(allocation.cardallocid)} className="bg-[#e64a32] hover:bg-[#e64a32] text-white font-bold py-2 px-4 rounded-full ml-1">Delete</button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Pagination */}
            <div className="flex justify-center mt-4">
                                <ReactPaginate
                                    previousLabel={"Previous"}
                                    nextLabel={"Next"}
                                    breakLabel={"..."}
                                    pageCount={Math.ceil(filteredAllocations.length / itemsPerPage)}
                                    marginPagesDisplayed={2}
                                    pageRangeDisplayed={5}
                                    onPageChange={handlePageClick}
                                    containerClassName={"pagination"}
                                    subContainerClassName={"pages pagination"}
                                    activeClassName={"active"}
                                    pageClassName={"inline-block px-3 py-1 border border-gray-300 bg-white text-black-700 hover:bg-blue-200"}
                                    pageLinkClassName={"page-link"}
                                    previousClassName={"inline-block px-3 py-1 border border-gray-300 bg-white text-black-700 hover:bg-blue-200"}
                                    previousLinkClassName={"page-link"}
                                    nextClassName={"inline-block px-3 py-1 border border-gray-300 bg-white text-black-700 hover:bg-blue-200"}
                                    nextLinkClassName={"page-link"}
                                    breakClassName={"inline-block px-3 py-1 border border-gray-300 bg-white text-black-700 hover:bg-blue-200 break-me"}
                                    breakLinkClassName={"page-link"}
                                    activeLinkClassName={"bg-blue-500 text-black"}
                                />
                                </div>
        </div>
    );
};

export default GameAllocationTable;
